import { api } from "~/trpc/react"
import { useOrganization } from "~/hooks/useOrganization"
import { usePermission } from "~/hooks/usePermission"
import { canAccessOrgSettings } from "~/lib/permissions"
import { resolveOrgPermissions } from "~/lib/permissionMasks"
import {
	HammerIcon,
	LogOutIcon,
	PlusCircleIcon,
	SettingsIcon
} from "lucide-react"
import { useNavigate } from "react-router-dom"
import { Link } from "~/components/Link"
import { EntityIdentity } from "~/components/EntityIdentity"
import { authClient, type Organization } from "~/lib/auth-client"
import { cn, compareNames } from "~/lib/utils"
import {
	Command,
	CommandEmpty,
	CommandGroup,
	CommandInput,
	CommandItem,
	CommandList,
	CommandSeparator
} from "./ui/command"

export function OrgSwitcher({
	orgs,
	withUserInfo = false,
	withInput = false,
	withCreate = false,
	onNavigate,
	className
}: {
	orgs: Organization[]
	withUserInfo?: boolean
	withInput?: boolean
	withCreate?: boolean
	onNavigate?: () => void
	className?: string
}) {
	const navigate = useNavigate()
	const { data: session } = authClient.useSession()
	const { organization } = useOrganization()
	const { orgPermissions } = usePermission()
	const memberships = api.user.getOrgs.useQuery(undefined, {
		enabled: !!session
	})
	return (
		<Command className={cn(className)}>
			{withInput ? (
				<CommandInput placeholder="Search organizations..." />
			) : null}
			<CommandList>
				{withInput ? (
					<CommandEmpty>No organizations found.</CommandEmpty>
				) : null}
				{withUserInfo && session ? (
					<>
						<CommandSeparator />
						<CommandGroup>
							<div className="px-2 py-1">
								<div className="flex items-center gap-2 group">
									<EntityIdentity
										kind="user"
										userId={session.user.id}
										name={session.user.name ?? session.user.email}
										image={session.user.image}
										size="md"
									/>
									<div className="flex flex-col">
										<span className="text-xs text-muted-foreground">
											<span className="italic group-hover:hidden">
												hover to reveal email
											</span>
											<span className="hidden group-hover:inline">
												{session.user.email}
											</span>
										</span>
									</div>
									<div className="flex items-center justify-end ml-auto gap-4">
										<a href="/logout">
											<LogOutIcon className="size-4 text-icons" />
										</a>
									</div>
								</div>
							</div>
						</CommandGroup>
					</>
				) : null}
				{orgs.length > 0 ? (
					<>
						<CommandSeparator />
						<CommandGroup>
							{[...orgs].sort(compareNames).map((org) => {
								const fullOrg = memberships.data?.find(
									(entry) => entry.id === org.id
								)
								const member = fullOrg?.members.find(
									(entry) => entry.userId === session?.user.id
								)
								const canOpenSettings =
									!!member &&
									canAccessOrgSettings(
										org.id === organization?.id
											? orgPermissions
											: resolveOrgPermissions(
													member.roles,
													fullOrg?.defaultPermissions
												).effectivePermissions
									)
								return (
									<CommandItem
										key={org.id}
										onSelect={() => {
											authClient.organization
												.setActive({
													organizationId: org.id
												})
												.then(() => {
													onNavigate?.()
													navigate(`/${org.slug}`)
												})
												.catch((error) => {
													console.error("Failed to switch organization:", error)
													// Stay on the current page if the switch fails
													window.location.reload()
												})
										}}
									>
										<EntityIdentity
											kind="organization"
											name={org.name}
											image={org.logo}
											size="md"
										/>
										{canOpenSettings && (
											<Link
												aria-label={`${org.name} settings`}
												to={`/${org.slug}/settings`}
												className="ml-auto z-50"
												onClick={(e) => {
													e.stopPropagation()
													onNavigate?.()
												}}
											>
												<SettingsIcon className="size-4" />
											</Link>
										)}
									</CommandItem>
								)
							})}
						</CommandGroup>
					</>
				) : null}
				{withCreate ? (
					<>
						<CommandSeparator />
						<CommandGroup>
							<CommandItem asChild>
								<Link to="/new" onClick={onNavigate}>
									<PlusCircleIcon className="w-4 h-4" /> Create an Organization
								</Link>
							</CommandItem>
						</CommandGroup>
					</>
				) : null}
				{session?.user.role === "admin" ? (
					<>
						<CommandSeparator />
						<CommandGroup>
							<CommandItem asChild>
								<Link to="/god" onClick={onNavigate}>
									<HammerIcon className="w-4 h-4" /> God Mode
								</Link>
							</CommandItem>
						</CommandGroup>
					</>
				) : null}
			</CommandList>
		</Command>
	)
}
